import { OntologyNode } from '../hooks/useResourceTree';

// 查找节点的完整路径和父文件夹信息
export const findNodeInfo = (nodes: OntologyNode[], targetKey: string, path: string[] = []): { parentId?: number, parentTitle?: string, path: string[] } | null => {
  for (const node of nodes) {
    const currentPath = [...path, node.title];
    if (node.key === targetKey) {
      return { path: currentPath };
    }
    if (node.children) {
      for (const child of node.children) {
        if (child.key === targetKey) {
          return { 
            parentId: node.id, 
            parentTitle: node.title, 
            path: [...currentPath, child.title] 
          };
        }
      }
      const result = findNodeInfo(node.children, targetKey, currentPath);
      if (result) return result;
    }
  }
  return null;
};

// 递归查找节点
export const findNodeRecursively = (nodes: OntologyNode[], targetKey: string): OntologyNode | null => {
  for (const node of nodes) {
    if (node.key === targetKey) {
      return node;
    }
    if (node.children) {
      const found = findNodeRecursively(node.children, targetKey);
      if (found) return found;
    }
  }
  return null;
};

// 获取默认文件夹ID
export const getDefaultFolderId = (ontologyList: OntologyNode[]): number | undefined => {
  const findDefaultFolder = (nodes: OntologyNode[]): OntologyNode | null => {
    for (const node of nodes) {
      if (node.type === 'folder' && node.title === 'Default') {
        return node;
      }
      if (node.children) {
        const found = findDefaultFolder(node.children);
        if (found) return found;
      }
    }
    return null;
  };
  
  const defaultFolder = findDefaultFolder(ontologyList);
  return defaultFolder?.id;
};

// 确定父目录ID：如果选中的是本体，则使用其父目录ID；否则使用选中目录的ID
export const determineParentId = (selectedNode: OntologyNode | null, ontologyList: OntologyNode[]): number | undefined => {
  if (selectedNode?.type === 'ontology') {
    // 如果当前选中的是本体，查找其父目录
    const parentInfo = findNodeInfo(ontologyList, selectedNode.key);
    return parentInfo?.parentId;
  } else {
    // 如果当前选中的是文件夹或未选中，使用选中目录的ID
    return selectedNode?.id;
  }
};

// 创建新节点（文件夹或本体）
export const createNewNode = (
  type: 'folder' | 'ontology', 
  selectedNode: OntologyNode | null, 
  ontologyList: OntologyNode[]
): OntologyNode => {
  const newNodeKey = `${type}-${Date.now()}`;
  const parentId = type === 'ontology' 
    ? (determineParentId(selectedNode, ontologyList) || getDefaultFolderId(ontologyList))
    : determineParentId(selectedNode, ontologyList);
  
  return {
    key: newNodeKey,
    title: '',
    type,
    children: [],
    isEditing: true,
    parentId: parentId,
  };
};

// 将新节点添加到正确的位置
export const addNodeToCorrectPosition = (
  nodes: OntologyNode[], 
  newNode: OntologyNode, 
  selectedNode: OntologyNode | null, 
  ontologyList: OntologyNode[]
): OntologyNode[] => {
  // 如果没有选中节点，直接添加到根级别
  if (!selectedNode) {
    return [...nodes, newNode];
  }
  
  // 如果选中的是本体，需要添加到其父目录下
  if (selectedNode.type === 'ontology') {
    const parentInfo = findNodeInfo(ontologyList, selectedNode.key);
    if (parentInfo && parentInfo.parentId) {
      // 找到父节点并添加到其子节点中
      const addToParentPosition = (nodes: OntologyNode[]): OntologyNode[] => {
        return nodes.map(node => {
          if (node.id === parentInfo.parentId) {
            return {
              ...node,
              children: [...(node.children || []), newNode]
            };
          } else if (node.children) {
            return {
              ...node,
              children: addToParentPosition(node.children)
            };
          }
          return node;
        });
      };
      
      return addToParentPosition(nodes);
    } else {
      // 没有找到父节点，添加到根级别
      return [...nodes, newNode];
    }
  } else {
    // 如果选中的是文件夹，添加到该目录下
    return nodes.map(node => {
      if (node.key === selectedNode.key) {
        return {
          ...node,
          children: [...(node.children || []), newNode]
        };
      } else if (node.children) {
        return {
          ...node,
          children: addNodeToCorrectPosition(node.children, newNode, selectedNode, ontologyList)
        };
      }
      return node;
    });
  }
};

// 从正确的位置删除元素
export const removeNodeFromCorrectPosition = (nodes: OntologyNode[], key: string): OntologyNode[] => {
  return nodes.map(node => {
    if (node.children) {
      return {
        ...node,
        children: removeNodeFromCorrectPosition(node.children, key).filter(child => child.key !== key)
      };
    }
    return node;
  }).filter(node => node.key !== key);
};

// 添加到默认文件夹或根级别
export const addNodeToDefaultOrRoot = (
  nodes: OntologyNode[], 
  newNode: OntologyNode,
  expandedKeys: React.Key[],
  setExpandedKeys?: (keys: React.Key[]) => void
): OntologyNode[] => {
  // 先尝试添加到默认文件夹
  const defaultFolder = nodes.find(node => 
    node.type === 'folder' && node.title === 'Default'
  );
  
  if (defaultFolder) {
    // 确保默认文件夹展开
    if (setExpandedKeys && !expandedKeys.includes(defaultFolder.key)) {
      setExpandedKeys([...expandedKeys, defaultFolder.key]);
    }
    return nodes.map(node => 
      node.key === defaultFolder.key
        ? { ...node, children: [...(node.children || []), newNode] }
        : node
    );
  } else {
    // 没有默认文件夹，添加到根级别
    return [...nodes, newNode];
  }
};

// 更新树中指定节点的标题（用于右侧修改名称后同步左侧列表）
export const updateNodeTitle = (
  nodes: OntologyNode[],
  targetKey: string,
  newTitle: string
): OntologyNode[] => {
  return nodes.map(node => {
    if (node.key === targetKey)
      return { ...node, title: newTitle };

    if (node.children)
      return { ...node, children: updateNodeTitle(node.children, targetKey, newTitle) };

    return node;
  });
};